/* eslint-disable react/no-danger */
import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react'
import QRCode from 'react-qr-code'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import block from 'bem-cn'

import { PaymentMethods, PaymentSystemNames } from 'services/api/PaymentApi/types/common'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'

import { ReactComponent as ArrowSvg } from 'shared/img/payment/arrow.svg'

import { defaultCurrency } from 'shared/constants'
import { AvailableCryptoCurrenciesList } from 'shared/models/Crypto'

import { selectLocaleDict } from 'features/locale/selectors'
import { clearDepositCreated, createDeposit } from 'features/payment/actions'
import { selectCreateDepositComm, selectDepositData } from 'features/payment/selectors'
import { selectLanguage } from 'features/userSettings/selectors'

import CopyButtonHoc from 'components/HOC/CopyButtonHoc/CopyButtonHoc'
import { useModalContext } from '@bcraft-frontend-lib/core'

import Loader from 'components/Loader/Loader'
import Button from 'components/UIKit/Button/Button'

import { getCryptoPaymentNotification } from './data/cryptoPaymentNotifications'

import './CryptoPayment.scss'

interface ICryptoPaymentProps {
  selectedCrypto: AvailableCryptoCurrenciesList
  isAgreed: boolean
  onGoBack: () => void
}

const b = block('crypto-payment')

export const CryptoPayment: React.FC<ICryptoPaymentProps> = ({ selectedCrypto, isAgreed, onGoBack }) => {
  const dispatch = useDispatch()
  const { closeModal } = useModalContext()

  const lang = useSelector(selectLanguage, shallowEqual)
  const locale = useSelector(selectLocaleDict, shallowEqual).payment
  const isDepositLoading = useSelector(selectCreateDepositComm, shallowEqual)?.[selectedCrypto]?.isLoading
  const paymentData = useSelector(selectDepositData, shallowEqual)

  const { cryptoAmount, fiatAmount, fiatCurrency } = useMemo(
    () => ({
      cryptoAmount: paymentData?.variables?.cryptoAmount ?? 0,
      fiatAmount: paymentData?.variables?.fiatAmount ?? 0,
      fiatCurrency: paymentData?.variables?.fiatCurrency ?? defaultCurrency,
    }),
    [paymentData?.variables],
  )

  const onClose = useCallback(() => {
    closeModal(MODAL_ROUTE_URLS.DEPOSIT())()
  }, [closeModal])

  useEffect(
    () => () => {
      dispatch(clearDepositCreated())
    },
    [],
  )

  useLayoutEffect(() => {
    if (isAgreed) {
      dispatch(
        createDeposit({
          amount: String(0),
          id: selectedCrypto,
          extra: {
            pay_currency: selectedCrypto,
          },
          system: PaymentSystemNames.NOWPAYMENTS,
          method: PaymentMethods.CRYPTO,
        }),
      )
    }
  }, [isAgreed])

  return (
    <div className={b()}>
      <div className={b('back')}>
        <span className={b('back-arrow')} onClick={onGoBack}>
          <ArrowSvg className={b('back-arrow-icon')} />
        </span>
        <span className={b('back-title')}>{locale.deposit}</span>
      </div>
      {!isAgreed && (
        <>
          <div dangerouslySetInnerHTML={getCryptoPaymentNotification(lang, selectedCrypto)} />
        </>
      )}
      {isAgreed && (
        <div className={b('container')}>
          <Loader loading={isDepositLoading} colorType="light" />
          {!isDepositLoading && paymentData?.walletAddress && (
            <div className={b('payment-data')}>
              <div className={b('address')}>
                <CopyButtonHoc text={paymentData.walletAddress}>{paymentData.walletAddress}</CopyButtonHoc>
              </div>

              <div className={b('minimum')}>
                <span>{`${locale.minimumPaymentAmount}:`}</span>
                <span>{`${cryptoAmount} ${selectedCrypto} (≈${fiatAmount} ${fiatCurrency})`}</span>
              </div>

              <div className={b('warning')}>
                {locale.transferAtLeast}
                <span>{`${cryptoAmount} ${selectedCrypto}`}</span>
                {locale.toThisAccountWarning}
              </div>

              <div className={b('qr-container')}>
                <QRCode value={paymentData.walletAddress} />
              </div>
            </div>
          )}
        </div>
      )}
      <div className={b('btn')}>
        <Button text={locale.depositCompleted} onClick={onClose} />
      </div>
    </div>
  )
}
